import { logger, monitoring } from 'common/services';

import { mapDevicesForOurApp } from './mapDevicesForOurApp';

export type MediaAccessResult = {
  hasAccess: boolean;
  wasPermissionDenied: boolean;
};

export const handleMediaAccessError = (error: unknown) => {
  if (error instanceof Error) {
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#exceptions
     * NotReadableError means that there might be an issue with a device which we'll handle separately,
     * specifically when the device wants to be accessed
     * Otherwise return false because other errors are related to document state, user denied access, etc.
     */

    logger.info('[checkMediaAccess] error while checking media access', { error });
    return error.name === 'NotReadableError';
  }

  logger.error('Unknown error while checking media access', { error });
  return false;
};

export const checkMediaAccess = async (
  constraints: MediaStreamConstraints = {
    audio: true,
    video: true,
  }
): Promise<boolean> => {
  try {
    logger.info('[checkMediaAccess] Checking media access');
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    disposeMediaStream(stream);
    logger.info('[checkMediaAccess] Media access granted');
    return true;
  } catch (error: unknown) {
    logger.warn('[checkMediaAccess] Media access failed', { error });
    const wasPermissionDenied = error instanceof Error && error.name === 'NotAllowedError';
    if (wasPermissionDenied) {
      monitoring.trackAction('devices.permission_denied');
    } else {
      monitoring.trackAction('devices.media_access_failed');
    }
    return handleMediaAccessError(error);
  }
};

export const disposeMediaStream = (stream: MediaStream) => {
  const tracks: MediaStreamTrack[] = stream.getTracks();
  for (const track of tracks) {
    try {
      track.stop();
      stream.removeTrack(track);
    } catch (e) {
      logger.error(e);
    }
  }
};

export const checkAudioAccess = async (): Promise<MediaAccessResult> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
    disposeMediaStream(stream);
    return { hasAccess: true, wasPermissionDenied: false };
  } catch (error: unknown) {
    const wasPermissionDenied = error instanceof Error && error.name === 'NotAllowedError';
    if (wasPermissionDenied) {
      monitoring.trackAction('devices.microphone.permission_denied');
    }
    return {
      hasAccess: handleMediaAccessError(error),
      wasPermissionDenied,
    };
  }
};

export const checkVideoAccess = async (): Promise<MediaAccessResult> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: true,
    });
    disposeMediaStream(stream);
    return { hasAccess: true, wasPermissionDenied: false };
  } catch (error: unknown) {
    const wasPermissionDenied = error instanceof Error && error.name === 'NotAllowedError';
    if (wasPermissionDenied) {
      monitoring.trackAction('devices.camera.permission_denied');
    }
    return {
      hasAccess: handleMediaAccessError(error),
      wasPermissionDenied,
    };
  }
};

export const getMediaDevices = async () => mapDevicesForOurApp(await navigator.mediaDevices.enumerateDevices());
